import { useContext, useEffect, useState } from 'react';
import { RequestDataContext } from '../context/RequestDataContext';
import { JSONObject } from '../types/types';
import { hasStringPlaceholder } from '../utils/json/hasStringPlaceholder';
import { resolveStringPlaceholders } from '../utils/json/resolveStringPlaceholders';

// Resolves JSONata placeholders in a label against the nearest scope's
// requestData — the same structure apiMethod templates see (filters, sort,
// datasetControl, …). Example: "Opravy {{ datasetControl.refLabel }}".
//
// Plain labels pass through untouched. A templated label renders nothing until
// its resolve finishes — the raw `{{ … }}` expression must never reach the
// screen.
//
// Raw useContext on purpose: outside a RequestDataProvider there is no scope
// data to resolve against, so the raw label is the only fallback left.
export const useResolvedLabel = (
  label: string | undefined
): string | undefined => {
  const { isProvided, requestData } = useContext(RequestDataContext);

  // `source` ties the resolved value to the label it was resolved from
  const [resolvedLabel, setResolvedLabel] = useState<{
    source: string;
    value: string;
    failed: boolean;
  } | null>(null);

  const isTemplated = hasStringPlaceholder(label);

  useEffect(() => {
    if (!label || !isTemplated || !isProvided) {
      setResolvedLabel(null);
      return;
    }

    let cancelled = false;
    resolveStringPlaceholders(label, (requestData ?? {}) as JSONObject)
      .then((resolved) => {
        if (cancelled) {
          return;
        }
        setResolvedLabel({
          source: label,
          value: resolved == null ? '' : String(resolved),
          failed: false,
        });
      })
      .catch((error) => {
        console.error('[useResolvedLabel] Failed to resolve label.', error);
        if (cancelled) {
          return;
        }
        // A broken expression is a config error — keep the raw label visible
        // so it is diagnosable instead of silently blanking the title.
        setResolvedLabel({ source: label, value: label, failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [label, isTemplated, isProvided, requestData]);

  if (!label || !isTemplated || !isProvided) {
    return label;
  }
  // Resolve still pending for this exact label — render nothing rather than
  // the raw expression. A re-resolve keeps the previous value on screen.
  if (resolvedLabel?.source !== label) {
    return undefined;
  }
  // An expression resolving to nothing (scope data not there yet) stays empty
  // until the data arrives and the re-resolve produces a value.
  return resolvedLabel.value || undefined;
};
